import { Fragment } from 'react';

import {
  Apartment as AdmissionIcon,
  AssignmentTurnedIn as ScreeningIcon,
  ExitToApp as DischargeIcon,
  ExpandMore as ExpandMoreIcon,
  GraphicEq as VoiceIcon,
  LocalHospital as OutpatientIcon,
  MeetingRoom as RoomChangeIcon,
  Psychology as SessionIcon,
  SwapHoriz as TypeChangeIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { type BgtHistoryDetail, getResidentBgtHistory } from '@/lib/api/bgt-history';
import {
  type ResidentTimeline,
  type TimelineEvent,
  type TimelineEventType,
  getResidentTimeline,
} from '@/lib/api/timeline';
import type { ResidentDto } from '@/lib/api/residents';
import { type VoiceTodayDetail, getResidentVoiceToday } from '@/lib/api/voice-today';
import { PROGRAM_LABEL } from '@/lib/program-labels';

import { BgtHistoryChart } from '../BgtHistoryChart';
import { HistorySummaryCard } from '../HistorySummaryCard';

interface Props {
  resident: ResidentDto | null;
}

const EVENT_META: Record<
  TimelineEventType,
  { label: string; color: string; Icon: typeof AdmissionIcon }
> = {
  ADMISSION: { label: '입소', color: '#2E7D32', Icon: AdmissionIcon },
  DISCHARGE: { label: '퇴소', color: '#9C27B0', Icon: DischargeIcon },
  BGT_SESSION: { label: `${PROGRAM_LABEL.bgt.short} 훈련`, color: '#1976D2', Icon: SessionIcon },
  VOICE_SESSION: { label: `${PROGRAM_LABEL.voice.short} 평가`, color: '#00897B', Icon: VoiceIcon },
  OUTPATIENT_VISIT: { label: '외래 방문', color: '#ED6C02', Icon: OutpatientIcon },
  RESIDENT_TYPE_CHANGED: { label: '구분 변경', color: '#0288D1', Icon: TypeChangeIcon },
  RESIDENT_ROOM_CHANGED: { label: '호실 변경', color: '#00838F', Icon: RoomChangeIcon },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/* ─────────────────────────────────────────────────────────────
 * 케어 여정 스테퍼 — 간이검사 → 입소 → BGT → 보이스웰니스
 * 단계 상태는 실제 데이터에서 도출(없으면 stub 안내).
 * ──────────────────────────────────────────────────────────── */

type JourneyTone = 'done' | 'active' | 'idle';

interface JourneyStage {
  key: string;
  label: string;
  status: string;
  tone: JourneyTone;
  color: string;
  Icon: typeof AdmissionIcon;
}

const TONE_DESC: Record<JourneyTone, { reached: boolean }> = {
  done: { reached: true },
  active: { reached: true },
  idle: { reached: false },
};

function StageNode({ stage }: { stage: JourneyStage }) {
  const reached = TONE_DESC[stage.tone].reached;
  const Icon = stage.Icon;
  return (
    <Stack alignItems="center" spacing={0.75} sx={{ width: 76, flexShrink: 0 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid',
          borderColor: reached ? stage.color : 'divider',
          bgcolor: reached ? `${stage.color}14` : 'action.hover',
          color: reached ? stage.color : 'text.disabled',
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ color: reached ? 'text.primary' : 'text.secondary' }}
      >
        {stage.label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: reached ? stage.color : 'text.disabled',
          textAlign: 'center',
          lineHeight: 1.2,
        }}
      >
        {stage.status}
      </Typography>
    </Stack>
  );
}

function Connector({ active }: { active: boolean }) {
  return (
    <Box
      sx={{
        flex: 1,
        height: 2,
        mt: '23px',
        minWidth: 12,
        borderRadius: 1,
        bgcolor: active ? 'success.main' : 'divider',
      }}
    />
  );
}

function CareJourney({ stages }: { stages: JourneyStage[] }) {
  return (
    <Stack direction="row" alignItems="flex-start" sx={{ px: 0.5 }}>
      {stages.map((stage, i) => (
        <Fragment key={stage.key}>
          <StageNode stage={stage} />
          {i < stages.length - 1 && <Connector active={TONE_DESC[stage.tone].reached} />}
        </Fragment>
      ))}
    </Stack>
  );
}

function EventRow({ event }: { event: TimelineEvent }) {
  const meta = EVENT_META[event.type];
  const Icon = meta.Icon;
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          mt: 0.5,
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: `${meta.color}15`,
          color: meta.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={600}>
            {meta.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(event.occurredAt)}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {event.summary}
        </Typography>
      </Stack>
    </Stack>
  );
}

/**
 * 의뢰서 5-2 탭5 통합 이력 — 케어 여정 개요 + 상세 이력 (P6-1).
 *
 * - 상단: 케어 여정 스테퍼(간이검사 → 입소 → BGT → 보이스웰니스)
 * - 보이스웰니스 현황: GET /voice/today 실데이터
 * - 하단: GET /residents/:id/timeline (BGT + VOICE 세션) + 최근 4주 BGT 요약
 */
export function TabHistory({ resident }: Props) {
  const { data, isLoading, isError } = useQuery<ResidentTimeline>({
    queryKey: ['residents', 'timeline', resident?.id],
    queryFn: () => getResidentTimeline(resident!.id),
    enabled: !!resident,
    staleTime: 60 * 1000,
  });

  // 의뢰서 5-2 탭5 — 최근 4주(28일) 요약. 탭2와 동일 endpoint 재사용으로 데이터 일관성 보장.
  const recentFrom = dayjs().startOf('day').subtract(27, 'day').format('YYYY-MM-DD');
  const recentTo = dayjs().startOf('day').format('YYYY-MM-DD');
  const recentQuery = useQuery<BgtHistoryDetail>({
    queryKey: ['residents', 'bgt-history', resident?.id, recentFrom, recentTo],
    queryFn: () => getResidentBgtHistory(resident!.id, recentFrom, recentTo),
    enabled: !!resident,
    staleTime: 60 * 1000,
  });

  const voiceTodayQuery = useQuery<VoiceTodayDetail>({
    queryKey: ['residents', 'voice-today', resident?.id],
    queryFn: () => getResidentVoiceToday(resident!.id),
    enabled: !!resident,
    staleTime: 30 * 1000,
  });

  if (!resident) return null;

  const hasBgt =
    (data?.events.some((e) => e.type === 'BGT_SESSION') ?? false) ||
    (recentQuery.data?.summary.activeDays ?? 0) > 0;

  const voiceLinked = voiceTodayQuery.data?.voiceUserId != null || resident.voiceUserId != null;
  const hasVoice =
    (data?.events.some((e) => e.type === 'VOICE_SESSION') ?? false) ||
    (voiceTodayQuery.data?.summary.hasData ?? false);

  const voiceStageStatus = !voiceLinked ? '미연결' : hasVoice ? '평가 중' : '연결됨';
  const voiceStageTone: JourneyTone = !voiceLinked ? 'idle' : hasVoice ? 'active' : 'done';

  const stages: JourneyStage[] = [
    {
      key: 'screening',
      label: '간이검사',
      status: '이력없음',
      tone: 'idle',
      color: '#1565C0',
      Icon: ScreeningIcon,
    },
    {
      key: 'admission',
      label: '입소',
      status: resident.admittedAt ? formatDate(resident.admittedAt) : '입소',
      tone: 'done',
      color: '#2E7D32',
      Icon: AdmissionIcon,
    },
    {
      key: 'bgt',
      label: 'BGT',
      status: hasBgt ? '훈련 중' : '미사용',
      tone: hasBgt ? 'active' : 'idle',
      color: '#7B1FA2',
      Icon: SessionIcon,
    },
    {
      key: 'voice',
      label: '보이스웰니스',
      status: voiceStageStatus,
      tone: voiceStageTone,
      color: '#00897B',
      Icon: VoiceIcon,
    },
  ];

  const voiceToday = voiceTodayQuery.data;
  const voiceNoDataMessage = !voiceLinked
    ? '보이스웰니스 계정이 연결되지 않았습니다. 입소자 수정에서 계정을 연결하세요.'
    : '오늘은 보이스웰니스 완료 세션이 없습니다.';

  return (
    <Stack spacing={2}>
      {/* 케어 여정 개요 */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2.5 }}>
            케어 여정
          </Typography>
          {isLoading ? (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={20} />
            </Stack>
          ) : (
            <CareJourney stages={stages} />
          )}
        </CardContent>
      </Card>

      {/* 입소 전 하하케어 이력 (연동 전 stub) */}
      <Accordion
        variant="outlined"
        disableGutters
        sx={{ borderRadius: '12px', overflow: 'hidden', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1565C0' }} />
            <Typography variant="subtitle2" fontWeight={700}>
              입소 전 하하케어 이력
            </Typography>
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary">
            하하케어 연동이 완료되면 입소 전 간이검사·방문 기록이 이 영역에 표시됩니다.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion
        variant="outlined"
        defaultExpanded
        disableGutters
        sx={{ borderRadius: '12px', overflow: 'hidden', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                bgcolor: '#00897B14',
                color: '#00897B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <VoiceIcon sx={{ fontSize: 16 }} />
            </Box>
            <Stack spacing={0}>
              <Typography variant="subtitle2" fontWeight={700}>
                보이스웰니스 현황
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {voiceToday?.summary.date ?? '금일 기준'}
              </Typography>
            </Stack>
            {voiceToday && (
              <Chip
                size="small"
                label={
                  voiceToday.summary.hasData ? '실시간' : voiceLinked ? '데이터 없음' : '미연결'
                }
                color={voiceToday.summary.hasData ? 'primary' : 'default'}
                variant="outlined"
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {voiceTodayQuery.isLoading ? (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={20} />
            </Stack>
          ) : voiceTodayQuery.isError ? (
            <Alert severity="error">보이스웰니스 현황을 불러오지 못했습니다</Alert>
          ) : !voiceToday || !voiceToday.summary.hasData ? (
            <Alert severity="info" variant="outlined">
              {voiceNoDataMessage}
            </Alert>
          ) : (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {voiceToday.scores.map((card) => (
                <Box
                  key={card.type}
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {card.label}
                    {card.status ? ` · ${card.status}` : ''}
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {card.todayScore === null ? '—' : `${card.todayScore}점`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    어제 {card.yesterdayScore === null ? '—' : `${card.yesterdayScore}점`}
                    {card.deltaPct !== null
                      ? ` · ${card.deltaPct > 0 ? '+' : ''}${card.deltaPct}%`
                      : ''}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      {/* 상세 이력 (실제 타임라인 이벤트) */}
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              상세 이력
            </Typography>
            <Chip size="small" label="최신순" variant="outlined" />
          </Stack>
          {isLoading ? (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={20} />
            </Stack>
          ) : isError ? (
            <Alert severity="error">타임라인을 불러오지 못했습니다</Alert>
          ) : !data || data.events.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 3 }}>
              아직 기록된 이벤트가 없습니다.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {data.events.map((e, i) => (
                <EventRow key={`${e.type}-${e.occurredAt}-${i}`} event={e} />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {recentQuery.isLoading ? (
        <Skeleton variant="rounded" height={240} />
      ) : recentQuery.data ? (
        <>
          <HistorySummaryCard
            title="최근 4주 요약"
            summary={recentQuery.data.summary}
            from={recentQuery.data.from}
            to={recentQuery.data.to}
          />
          <BgtHistoryChart days={recentQuery.data.days} />
        </>
      ) : null}
    </Stack>
  );
}
